import styled from "@emotion/styled";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { setFiltersApplied, setSelectedFunctionalAreas } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import AsyncAutocomplete from "components/common/Autocomplete";
import Button from "components/common/Button";
import Feedback from "components/common/Feedback";
import ButtonLink from "components/common/Link";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Typography from "components/common/Typography";
import AssignMultiplePlansDialog from "components/common/plans/AssignMultiplePlansDialog";
import { uniq } from "lodash";
import { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { FEEDBACK_TIMER } from "utils/CommonConstants";
import { getListOfFilters } from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import DeleteDialog from "./deleteInncodeDialog";
import MultiSelectFilter from "./multiSelectFilter";
import {
  deleteMapping,
  fetchMyListDetails,
  mapUserToInncode,
  setSMEFilters,
  setShowAddInncodeDialog,
  setShowDeleteInncodeDialog
} from "./sme.slice";

const StyledLink = styled(Link)`
  font-family: Noto Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  text-decoration: none;
  color: ${tokens.Text.General.PrimaryHeadline};
  text-transform: uppercase;
`;

const Users = (props: any) => {
  const dispatch = useAppDispatch();
  const [isEditing, setEditing] = useState<boolean>(false);
  const { users } = useAppSelector((state) => state.sme);
  const name = users?.length
    ? /* @ts-ignore */
      users.filter((el: any) => el.hltId === props?.data?.hltId)[0]?.displayName
    : "";

  const handleAssignUserToInncode = (hltId: string, groupId: string, id: string) => {
    if (props?.data?.hltId !== hltId) {
      const payload = { mappings: [{ hltId, id }] };
      dispatch(mapUserToInncode({ groupId, payload, view: "inncodes" }));
    }

    setEditing(false);
  };

  return (
    <>
      {isEditing && (
        <AsyncAutocomplete
          getUserDetail={(val: any) => handleAssignUserToInncode(val[0], props?.groupId, props?.data?.id)}
          placeholder="Search a user"
          onBlur={() => setEditing(false)}
          autoFocus
          searchParams={{ innCode: props?.data?.innCode }}
        />
      )}

      {!isEditing && (
        <>
          {" "}
          {props?.data?.hltId ? (
            <Stack
              spacing={0.5}
              direction="row"
              alignItems="center"
              sx={{
                backgroundColor: tokens.Background.General.Color,
                height: "32px",
                display: "inline-flex",
                paddingX: "8px",
                borderRadius: "8px",
                color: tokens.Text.General.Color,
                cursor: "pointer"
              }}
              onClick={(e) => {
                setEditing(true);
                e.stopPropagation();
              }}
            >
              <Typography variant="body2" sx={{ textWrap: "nowrap" }}>
                {name}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{ color: tokens.Text.General.Color, fontSize: "18px", paddingTop: "2px", cursor: "pointer" }}
              />
            </Stack>
          ) : (
            <Stack
              component="a"
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ cursor: "pointer" }}
              onClick={() => setEditing(true)}
            >
              <AccountCircleOutlinedIcon sx={{ fontSize: "18px", color: tokens.Icon.General.Secondary }} />
              <Typography variant="body2" color={tokens.Text.General.Secondary}>
                Add user name
              </Typography>
            </Stack>
          )}
        </>
      )}
    </>
  );
};

const MyListTable = (props: any) => {
  const { listId } = props;
  const dispatch = useAppDispatch();
  const [pageNo, setPageNo] = useState(0);
  let [searchParams] = useSearchParams();
  const { isMobileScreen } = useContext(ThemeContext);
  const [rowSelection, setRowSelection] = useState<any[]>([]);
  const { myListDetails, filters, showDeleteInncodeDialog, mappingId } = useAppSelector((state) => state.sme);
  const { mappings, meta } = myListDetails;
  // @ts-ignore
  const [multiplansAssign, setMultiplansAssign] = useState(false);
  const [checkInncodeUser, setCheckInncodeUser] = useState({
    shouldShow: false,
    variant: "error",
    timer: FEEDBACK_TIMER
  });
  const isFiltered = filters.hasOwnProperty("engagement") || filters.hasOwnProperty("innCode");

  const { filtersApplied } = useAppSelector((state: any) => state.app);
  const handleResetDetails = () => {
    setRowSelection([]);
    setPageNo(0);
    dispatch(setSelectedFunctionalAreas([]));
  };

  useEffect(() => {
    handleResetDetails();
  }, [listId]);

  const handleCloseDialog = () => {
    setMultiplansAssign(false);
  };

  const handleOpenDeleteDialog = (id: any) => {
    dispatch(setShowDeleteInncodeDialog({ show: true, id }));
  };

  const handleRemoveMapping = (id: any) => {
    const innCodes = filters?.innCode.join(",");
    const engagements = filters?.engagement.join(",");

    dispatch(
      deleteMapping({
        mappingIds: [id],
        listId,
        view: "inncodes",
        page: pageNo,
        engagement: engagements || "",
        innCode: innCodes || ""
      })
    ).then((res: any) => {
      if (res?.meta?.requestStatus === "fulfilled") {
        setRowSelection((arr) => arr?.filter((item: any) => item.id !== id));
      }
    });
  };

  const handleFilterChange = (val: any, key: string) => {
    if (val.length > 0) {
      dispatch(setFiltersApplied(true));
    } else if (getListOfFilters().length === 1) dispatch(setFiltersApplied(false));

    const id = searchParams.get("id");
    const innCodes = key === "innCode" ? val.join(",") : filters?.innCode.join(",");
    const engagements = key === "engagement" ? val.join(",") : filters?.engagement.join(",");

    dispatch(setSMEFilters({ ...filters, [key]: val.length ? val : [] }));
    dispatch(
      fetchMyListDetails({
        id,
        view: "inncodes",
        engagement: engagements || "",
        innCode: innCodes || ""
      })
    );
    setPageNo(0);
  };

  const fetchMore = () => {
    const id = searchParams.get("id");
    setPageNo(pageNo + 1);
    dispatch(fetchMyListDetails({ id, view: "inncodes", page: pageNo + 1 }));
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setRowSelection(mappings);
      return;
    }

    setRowSelection([]);
  };

  const isSelected = (item: number) => rowSelection.findIndex((r: any) => r.id === item) !== -1;

  const handleSelect = (event: any, item: any) => {
    const selectedIndex = rowSelection.findIndex((r: any) => r.id === item.id);
    let newSelected: any = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(rowSelection, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(rowSelection.slice(1));
    } else if (selectedIndex === rowSelection.length - 1) {
      newSelected = newSelected.concat(rowSelection.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(rowSelection.slice(0, selectedIndex), rowSelection.slice(selectedIndex + 1));
    }

    setRowSelection(newSelected);
  };

  return (
    <>
      <Feedback
        form=""
        actionStatus={checkInncodeUser}
        message="User is not mapped to some Inncodes that you have selected"
        onCloseChange={() => setCheckInncodeUser({ shouldShow: false, variant: "error", timer: FEEDBACK_TIMER })}
      />
      <Stack
        direction={"row"}
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
        flexWrap={isMobileScreen ? "wrap" : "nowrap"}
        sx={{
          padding: "16px 24px",
          borderBottom: `1px solid ${tokens.Border.General.Tertiary}`,
          backgroundColor: tokens.Background.General.Primary
        }}
      >
        {filtersApplied && (
          <Grid item xs={12} sm={1}>
            {" "}
            <ButtonLink
              text="Clear all"
              sx={{ color: tokens.Text.General.Color }}
              textVariant="body2"
              underline="none"
              onClick={() => {
                const id = searchParams.get("id");
                dispatch(
                  setSMEFilters({
                    innCode: [],
                    engagement: [],
                    statsEngagement: [],
                    planStatus: []
                  })
                );
                dispatch(
                  fetchMyListDetails({
                    id,
                    view: "inncodes",
                    engagement: "",
                    innCode: ""
                  })
                );
                dispatch(setFiltersApplied(false));
              }}
            />
          </Grid>
        )}
        <MultiSelectFilter
          label="Inncode"
          onSubmit={(v: any) => handleFilterChange(v, "innCode")}
          // @ts-ignore
          options={uniq(meta?.filters).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "accent" }))}
          isFiltered={isFiltered}
          upperCase
          selectedOptions={filters?.innCode}
        />

        <MultiSelectFilter
          label="Engagement"
          options={["Low", "Medium", "High"]}
          onSubmit={(v: any) => handleFilterChange(v, "engagement")}
          isFiltered={isFiltered}
          selectedOptions={filters?.engagement}
        />
      </Stack>
      {mappings?.length ? (
        <Box
          sx={{
            backgroundColor: tokens.Background.General.Primary,
            position: "relative",
            width: isMobileScreen ? "100%" : "calc(100vw - 440px)"
          }}
        >
          <Scroller
            dataLength={mappings.length}
            loadMore={fetchMore}
            totalList={meta?.page?.total}
            label=""
            footerStyle={{ marginY: "16px" }}
            scrollableTarget="tableDiv"
          >
            <TableContainer sx={{ height: "100%", width: "100%" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: "32px !important"
                      }}
                      component="th"
                    >
                      <Checkbox
                        className="tbl-checkbox"
                        indeterminate={rowSelection.length > 0 && rowSelection.length < mappings.length}
                        checked={rowSelection.length === mappings.length}
                        sx={{
                          "&.tbl-checkbox": {
                            color: tokens.Border.General.Secondary
                          },
                          "&.tbl-checkbox.Mui-checked, &.tbl-checkbox.MuiCheckbox-indeterminate": {
                            color: tokens.Border.General.Color
                          }
                        }}
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell component="th">Inncode</TableCell>
                    <TableCell component="th" sx={{ minWidth: "300px" }}>
                      User
                    </TableCell>
                    <TableCell component="th">Last Assignment</TableCell>
                    <TableCell component="th">Active Plans</TableCell>
                    <TableCell component="th">Completed Plans</TableCell>
                    <TableCell component="th">Engagement</TableCell>
                    <TableCell component="th" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((list: any) => {
                    const isItemSelected = isSelected(list.id);

                    return (
                      <TableRow key={list.id}>
                        <TableCell>
                          <Checkbox
                            className="tbl-checkbox"
                            checked={isItemSelected}
                            onChange={(e: any) => handleSelect(e, list)}
                            sx={{
                              "&.tbl-checkbox": {
                                color: tokens.Border.General.Secondary
                              },
                              "&.tbl-checkbox.Mui-checked, &.tbl-checkbox.MuiCheckbox-indeterminate": {
                                color: tokens.Border.General.Color
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {list.plansCount ? (
                            <StyledLink
                              to={`/inncodestats/${list.id}/${listId}`}
                              color={tokens.Text.General.PrimaryHeadline}
                            >
                              {list.innCode}
                            </StyledLink>
                          ) : (
                            <Typography
                              variant="body2"
                              showBold
                              color={tokens.Text.General.PrimaryHeadline}
                              sx={{ textTransform: "uppercase", maxWidth: "100px", flex: 1 }}
                            >
                              {list.innCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Users data={list} groupId={listId} />
                        </TableCell>
                        <TableCell>
                          {list?.lastAssignmentDate ? getUIFormatDate(list?.lastAssignmentDate) : "-"}
                        </TableCell>
                        <TableCell> {list?.activePlans || "-"}</TableCell>
                        <TableCell> {list?.completedPlans || "-"}</TableCell>
                        <TableCell> {list?.engagement || "-"}</TableCell>
                        <TableCell>
                          <IconButton aria-label="remove" onClick={() => handleOpenDeleteDialog(list?.id)}>
                            <HighlightOffIcon sx={{ color: tokens.Icon.General.Secondary, fontSize: "20px" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scroller>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              position: "absolute",
              height: "60px",
              backgroundColor: tokens.Background.General.Primary,
              bottom: "-60px",
              left: 0,
              right: 0,
              paddingLeft: "24px",
              paddingRight: "48px",
              borderTop: `1px solid ${tokens.Border.General.Tertiary}`
            }}
          >
            <Button
              variant="secondary"
              disabled={!rowSelection?.length}
              label={rowSelection.length > 0 ? `Assign Plans (${rowSelection.length})` : `Assign Plans`}
              onClick={() => {
                if (rowSelection.every((item: any) => item.hltId)) {
                  setMultiplansAssign(true);
                } else {
                  setCheckInncodeUser({ shouldShow: true, variant: "error", timer: FEEDBACK_TIMER });
                }
              }}
            />
          </Stack>

          {<DeleteDialog open={showDeleteInncodeDialog} deleteMapping={() => handleRemoveMapping(mappingId)} />}

          {multiplansAssign && rowSelection.length > 0 && (
            <AssignMultiplePlansDialog
              setOpenAssignPlanDialog={setMultiplansAssign}
              selectedInncodeList={rowSelection}
              handleCloseDialog={handleCloseDialog}
              listId={listId}
              onCloseConfirm={handleResetDetails}
              flowSpecificPayload={{
                isBulkAssignment:
                  rowSelection?.length > 0 && rowSelection?.every(({ innCode }) => innCode === rowSelection[0].innCode)
                    ? false
                    : true,
                innCode:
                  rowSelection?.length > 0 && rowSelection?.every(({ innCode }) => innCode === rowSelection[0].innCode)
                    ? rowSelection[0]?.innCode
                    : "CORP",
                parent: "myDashboard"
              }}
            />
          )}
        </Box>
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            height: "100%",
            backgroundColor: tokens.Background.General.Primary
          }}
        >
          <Typography variant="h5" color={tokens.Text.General.Secondary} sx={{ marginBottom: "20px" }}>
            No data to display
          </Typography>
          {!isFiltered && <Button label="Add Inncode" onClick={() => dispatch(setShowAddInncodeDialog(true))} />}
        </Stack>
      )}
    </>
  );
};

export default MyListTable;
